<script setup lang="ts">
import { computed } from "vue"
import { useLocalePath } from "#i18n"
import { useConverters } from '~/composables/useConverters'
import { useDeveloperSignup } from '~/composables/useDeveloperSignup'

const props = withDefaults(defineProps<{
  video: Video;
  lazy?: boolean;
}>(), {
  lazy: true,
});

const localePath = useLocalePath()
const { releasedAgo, formatDuration } = useConverters()
const { $wadAcl } = useNuxtApp()
const { protectedVideoLoginRedirectUrl } = useDeveloperSignup()

const released = computed(() => {
  const date = props.video.release_date;
  const eventName = props.video.event_type_name;
  const ago = date ? releasedAgo(date) : '';
  return eventName ? `${ago} • ${eventName}` : ago;
});
const showLocked = computed(() => {
  return props.video.requires_login_status && !$wadAcl.isAdmin() && !$wadAcl.isCompany() && !$wadAcl.isDeveloper()
});

</script>

<template>
  <nuxt-link class="wad4-video-card" :href="localePath({name: 'videos-id-slug', params: { id: video.id, slug: video.slug }})">
    <div class="wad4-video-card__thumbnail">
      <ClientOnly>
        <div class="wad4-video-card__locked" v-if="showLocked">
          <div class="wad4-video-card__locked__text">
            <a class="wad4-video-card__locked__text--link" :href="protectedVideoLoginRedirectUrl(video)">Log in</a> to watch
          </div>
          <svg class="wad4-video-card__locked__svg-lock" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M12 1.5C10.6076 1.5 9.27225 2.05312 8.28769 3.03769C7.30312 4.02226 6.75 5.35761 6.75 6.75V9.75C5.95435 9.75 5.19129 10.0661 4.62868 10.6287C4.06607 11.1913 3.75 11.9544 3.75 12.75V19.5C3.75 20.2956 4.06607 21.0587 4.62868 21.6213C5.19129 22.1839 5.95435 22.5 6.75 22.5H17.25C18.0456 22.5 18.8087 22.1839 19.3713 21.6213C19.9339 21.0587 20.25 20.2956 20.25 19.5V12.75C20.25 11.9544 19.9339 11.1913 19.3713 10.6287C18.8087 10.0661 18.0456 9.75 17.25 9.75V6.75C17.25 3.85 14.9 1.5 12 1.5ZM15.75 9.75V6.75C15.75 5.75544 15.3549 4.80161 14.6517 4.09835C13.9484 3.39509 12.9946 3 12 3C11.0054 3 10.0516 3.39509 9.34835 4.09835C8.64509 4.80161 8.25 5.75544 8.25 6.75V9.75H15.75Z" fill="currentColor"/>
          </svg>
          <svg class="wad4-video-card__locked__svg-open" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 1.5C20.9 1.5 23.25 3.85 23.25 6.75V10.5C23.25 10.6989 23.171 10.8897 23.0303 11.0303C22.8897 11.171 22.6989 11.25 22.5 11.25C22.3011 11.25 22.1103 11.171 21.9697 11.0303C21.829 10.8897 21.75 10.6989 21.75 10.5V6.75C21.75 5.75544 21.3549 4.80161 20.6517 4.09835C19.9484 3.39509 18.9946 3 18 3C17.0054 3 16.0516 3.39509 15.3483 4.09835C14.6451 4.80161 14.25 5.75544 14.25 6.75V9.75C15.0456 9.75 15.8087 10.0661 16.3713 10.6287C16.9339 11.1913 17.25 11.9544 17.25 12.75V19.5C17.25 20.2956 16.9339 21.0587 16.3713 21.6213C15.8087 22.1839 15.0456 22.5 14.25 22.5H3.75C2.95435 22.5 2.19129 22.1839 1.62868 21.6213C1.06607 21.0587 0.75 20.2956 0.75 19.5V12.75C0.75 11.9544 1.06607 11.1913 1.62868 10.6287C2.19129 10.0661 2.95435 9.75 3.75 9.75H12.75V6.75C12.75 3.85 15.1 1.5 18 1.5Z" fill="currentColor"/>
            <defs>
              <clipPath id="clip0_11049_15900">
                <rect width="24" height="24" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </div>
      </ClientOnly>
      <img :src="video.img_url" :alt="video.title" :fetchpriority="lazy ? 'auto' : 'high'" :loading="lazy ? 'lazy' : 'eager'" class="wad4-video-card__thumbnail-image"/>
      <span class="wad4-video-card__thumbnail-duration">{{ formatDuration(video.duration) }}</span>
    </div>
    <div class="wad4-video-card__info">
      <CommonEventIcon :eventCategory="props.video.event_category" />
      <div class="wad4-video-card__info__text">
        <h3 class="wad4-video-card__info-title">{{ video.title }}</h3>
        <p v-if="video.speaker_name" class="wad4-video-card__info-speaker">{{ video.speaker_name }}</p>
        <time class="wad4-video-card__info-release" :datetime="video.release_date"> about {{ released }}</time>
      </div>
    </div>
  </nuxt-link>
</template>

<style lang="scss">
.wad4-video-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: $wad4-spacing-md;
  text-decoration: none !important;
  width: $wad4-col-4;

  @include mqBreakPoint(1536px) {
    flex: 0 0 354px;
  }

  &:hover {
    .wad4-video-card__info-title{
      margin: 0;
      color: $wad4-c-text-text-brand-tertiery;
    }
    .wad4-video-card__thumbnail-image {
      transform: scale(1.05);
    }
  }



  &__clickable {
    width: 100%;
    text-decoration: none !important;
  }

  &__title-wrapper {
    display: flex;
    position: relative;
  }

  &__thumbnail {
    position: relative;
    overflow: hidden;
    border-radius: $wad4-border-radius-md;
    aspect-ratio: 16/9;
    background-color: $wad4-c-background-bg-disabled;
    width: 100%;

    &-duration {
      font-family: $wad4-default-font-mono;
      position: absolute;
      right: 9px;
      bottom: 9px;
      font-size: 12px;
      font-weight: $wad4-font-weight-regular-bold;
      text-align: center;
      color: $wad4-c-text-text-white;
      background-color: $wad4-c-background-bg-black;
      display: flex;
      padding: $wad4-spacing-xs $wad4-spacing-md;
      justify-content: center;
      align-items: center;
      gap: $wad4-spacing-md;
      backdrop-filter: blur(4px);
      border-radius: $wad4-border-radius-xs;
    }

    &-image {
      max-width: 100%;
      height: auto;
      transition: transform 0.3s ease;
      object-fit: cover;
      border-radius: $wad4-border-radius-md;
    }
  }

  &__locked {
    position: absolute;
    display: flex;
    font-family: $wad4-default-font-sans;
    align-items: center;
    background-color: $wad4-c-background-bg-brand-primary;
    border-radius: 26px;
    width: fit-content;
    margin-right: $wad4-spacing-xl;
    margin-top: $wad4-spacing-xl;
    right: 0;
    padding: $wad4-spacing-md;
    z-index: 5;

    &__text {
      color: $wad4-c-text-text-brand-tertiery !important;
      font-size: 14px;
      line-height: 18px;
      margin-right: $wad4-spacing-md;
      &--link {
        color: $wad4-c-text-text-brand-tertiery !important;
        cursor: pointer;
        z-index: 7;
      }
      a.wad4-video-card__locked__text--link {
        text-decoration: underline;
        text-decoration-skip-ink: none;
      }
    }

    &:hover {
      .wad4-video-card__locked__svg-lock {
        display: none;
      }
      .wad4-video-card__locked__text {
        display: block;
      }
      .wad4-video-card__locked__svg-open {
        display: block;

      }
      // Padding on hover
      padding-left: $wad4-spacing-xl;
      padding-right: $wad4-spacing-md;
    }

    &__text,
    &__svg-open {
      display: none;
    }
  }

  &__info {
    display: flex;
    align-items: flex-start;
    gap: $wad4-spacing-md;

    &__text{
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: $wad4-spacing-xs;
      flex: 1 0 0;

      @media (max-width: $wad4-breakpoint-mobile-max) {
        text-overflow: ellipsis;
      }
    }

    &-speaker {
      font-size: 0.875rem;
      line-height: 1.25rem;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      color: $wad4-c-text-text-secondary;
      font-family: $wad4-default-font-sans;
      margin: 0;
    }

    &-release {
      color: $wad4-c-text-text-tertiery;
      font-size: 0.75rem;
    }

    &-title {
      display: flex;
      flex-direction: column;
      justify-content: center;
      color: $wad4-c-text-text-primary;
      font-size: 1rem;
      line-height: 1.25rem;
      font-weight: $wad4-font-weight-semi-bold;
      margin: 0;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

}
</style>
